import {
  Box,
  BoxProps,
  Button,
  Center,
  createIcon,
  Flex,
  List,
  ListItem,
  ListItemProps,
  Text,
  useColorModeValue as mode,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import { FiUnlock } from "react-icons/fi";
import * as React from "react";
import Payment from "components/payment";
import { useSession } from "next-auth/react";

const CheckIcon = createIcon({
  viewBox: "0 0 17 12",
  d: "M0 5.82857L1.64571 4.11429L5.48571 7.2L14.8114 0L16.4571 1.71429L5.48571 12L0 5.82857Z",
});

const PricingDetail = (props: ListItemProps) => {
  const { children, ...rest } = props;
  return (
    <ListItem
      display="flex"
      alignItems="flex-start"
      fontWeight="medium"
      // maxW="260px"
      {...rest}
    >
      <CheckIcon mr="4" mt="1" color="white" />
      <Text as="span" display="inline-block">
        {children}
      </Text>
    </ListItem>
  );
};

const PopularBadge = (props: BoxProps) => (
  <Box
    whiteSpace="nowrap"
    top="-4"
    left="50%"
    transform="translateX(-50%)"
    pos="absolute"
    rounded="md"
    fontWeight="bold"
    fontSize="sm"
    px="4"
    py="1"
    textTransform="uppercase"
    bg="blue.500"
    color="white"
    {...props}
  />
);

interface PriceDisplayProps {
  currency: string;
  price: number | string;
  billingFrequency: "yearly" | "monthly";
}

const PriceDisplay = (props: PriceDisplayProps) => {
  const { currency, price, billingFrequency } = props;
  return (
    <>
      <Flex
        w="100%"
        align="center"
        justify="center"
        mt="5"
        mb={2}
        fontWeight="extrabold"
      >
        <Text fontSize="4xl" mr="2">
          {currency}
        </Text>
        <Text fontSize="72px" lineHeight="1" letterSpacing="tight">
          {price}
        </Text>
      </Flex>
      <Text
        textAlign="center"
        fontSize="24px"
        lineHeight="1"
        letterSpacing="tight"
        mb={4}
      >
        {billingFrequency === "yearly" && (
          <Box color="gray.400" fontSize="lg">
            billed {currency}
            {Number(Number(price) * 12).toFixed(0)} annually
          </Box>
        )}
      </Text>
    </>
  );
};

const PricingWrapper = (props: BoxProps & { highlight?: boolean }) => {
  const { highlight, ...rest } = props;

  const popularStyles: BoxProps = {
    borderWidth: "2px",
    borderColor: "blue.500",
    zIndex: 1,
    px: "8",
    pt: "12",
    pb: "10",
    top: { lg: "-8" },
  };

  const styles = highlight ? popularStyles : null;

  return (
    <Box
      w="full"
      maxW="md"
      mx="auto"
      bg={mode("white", "gray.700")}
      px="10"
      pt="8"
      pb="8"
      rounded="lg"
      shadow="base"
      position="relative"
      {...styles}
      {...rest}
    />
  );
};

interface PricingCardProps extends BoxProps {
  features: string[];
  popular?: boolean;
  name: string;
  description: string;
  price: number | string;
  billingFrequency: "monthly" | "yearly";
  onChangeBillingFrequency: () => void;
  onClick?: () => void;
}

export const PricingCard = (props: PricingCardProps) => {
  const {
    onClick,
    features,
    name,
    description,
    price,
    popular,
    billingFrequency,
    onChangeBillingFrequency,
    //@ts-ignore
    onClickStartFreeTrial=undefined,
    ...rest
  } = props;

  const { data: session } = useSession() ?? {}
  return (
    <PricingWrapper highlight={popular} {...rest} bg={mode("gray.700", "gray.700")}>
      <Flex direction="column" justify="center" >
        <Text align="center" fontSize="lg" fontWeight="bold" color="white">
          Take your first step towards better tweets and growing your audience.
        </Text>
      </Flex>
      <List stylePosition="outside" mt="8" spacing={4}>
        {features.map((feature, idx) => (
          <PricingDetail color="white" key={idx}>{feature}</PricingDetail>
        ))}
      </List>
      <Center mt={8}>
        <Flex direction="column" justify="center" alignItems="center">
          <Payment
            color={mode("gray", "twitter")}
            plan={billingFrequency === "yearly" ? "startYearly" : "startMonthly"}
            billingFrequency={billingFrequency}
            session={session}
            customCancelUrl="onboard"
            onClickStartFreeTrial={onClickStartFreeTrial}
            source="onboard"
          />
          <Flex>
            <Text
              align="center"
              mt="5"
              color={mode("gray.100", "gray.400")}
              // maxW="16rem"
              mx="auto"
              // fontWeight="bold"
              fontSize="lg"
            >
              Then
            </Text>
            <Text
              align="center"
              mt="5"
              color={mode("gray.100", "gray.400")}
              mx="auto"
              // fontWeight="bold"
              fontSize="lg"
              textDecoration="line-through"
              ml={2}
              mr={2}
            >
              {billingFrequency === "yearly" ? "$14" : "$19"}
            </Text>
            <Text
              align="center"
              mt="5"
              color={mode("gray.100", "gray.400")}
              // maxW="16rem"
              mx="auto"
              // fontWeight="bold"
              fontSize="lg"
            >
              ${price} / month
            </Text>
          </Flex>
          {/* <Box>
            <Text
              textAlign="center"
              lineHeight="1"
              letterSpacing="tight"
              mb={0}
              mt={2}
              minH={5}
            >
              {billingFrequency === "yearly" && (
                <Box color="gray.400" fontSize="14px">
                  billed $
                  {Number(Number(price) * 12).toFixed(0)} annually
                </Box>
              )}
            </Text>
            <Box
              onClick={onChangeBillingFrequency}
              as="button"
              display="inline-flex"
              mx="auto"
              color={mode("white", "#1E1E1E")}
              fontWeight="400"
              fontSize="15px"
              textDecoration="underline"
            >
              {`Switch to ${
                billingFrequency === "monthly" ? "yearly" : "monthly"
              } billing`}
            </Box>
          </Box> */}
          <Text
            align="center"
            mt="5"
            color={mode("gray.100", "gray.400")}
            // maxW="16rem"
            mx="auto"
            fontStyle="italic"
            fontSize="sm"
          >
            100% money-back guarantee.
          </Text>
          <Text
            align="center"
            color={mode("gray.100", "gray.400")}
            // maxW="16rem"
            mx="auto"
            fontStyle="italic"
            fontSize="sm"
          >
            no questions asked.
          </Text>
        </Flex>
      </Center>
    </PricingWrapper>
  );
};
